/**
 * Riot Sign On (RSO) OAuth2 Configuration
 *
 * To obtain these values, you need an approved production application
 * on the Riot Developer Portal (https://developer.riotgames.com)
 * with an RSO client assigned.
 *
 * - RSO_CLIENT_ID: Your RSO client ID from the Riot Developer Portal
 * - RSO_REDIRECT_URI: Must match exactly what is registered in your RSO client settings
 */

export const RSO_CLIENT_ID = "6991fa44-ab74-42be-bbe7-8170803ae22c";

export const RSO_REDIRECT_URI = `${window.location.origin}/riot-callback`;

const RSO_AUTH_BASE = "https://auth.riotgames.com/authorize";
const RSO_TOKEN_URL = "https://auth.riotgames.com/token";

// ---------------------------------------------------------------------------
// PKCE helpers (required for public clients / SPAs)
// ---------------------------------------------------------------------------

function base64UrlEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...buffer))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

function generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return base64UrlEncode(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Auth URL & token exchange
// ---------------------------------------------------------------------------

/**
 * Builds the full Riot authorization URL with PKCE.
 * Stores the code verifier in localStorage so the popup callback can use it.
 */
export async function buildRsoAuthUrl(): Promise<string> {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    localStorage.setItem("rso_code_verifier", codeVerifier);

    const params = new URLSearchParams({
        redirect_uri: RSO_REDIRECT_URI,
        client_id: RSO_CLIENT_ID,
        response_type: "code",
        scope: "openid",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
    });
    return `${RSO_AUTH_BASE}?${params.toString()}`;
}

/**
 * Exchanges the authorization code for tokens at Riot's token endpoint.
 * Uses the PKCE code verifier stored during the authorize step.
 */
export async function exchangeCodeForTokens(
    code: string
): Promise<{ access_token: string; refresh_token: string; expires_in: number }> {
    const codeVerifier = localStorage.getItem("rso_code_verifier");

    const body = new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: RSO_REDIRECT_URI,
        client_id: RSO_CLIENT_ID,
    });

    if (codeVerifier) {
        body.append("code_verifier", codeVerifier);
        localStorage.removeItem("rso_code_verifier");
    }

    const response = await fetch(RSO_TOKEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Token exchange failed (HTTP ${response.status})`);
    }

    return response.json();
}
